/**
 * @quotech/api - Shared Hub API Setup
 *
 * Replaces the near-identical `setupApi.ts` found in every hub.
 * Call `setupHubApi({ hubName: 'CRM' })` once in main.tsx.
 *
 * Token resolution order:
 * 1. Cookie (set by SessionManager, survives Zustand rehydration race)
 * 2. Zustand store (@quotech/auth)
 *
 * On 401: redirects to master login unless already on dashboard.quotech.ai.
 */

import type { InternalAxiosRequestConfig } from 'axios'
import { setupDefaultClient, getDefaultClient } from './client'
import type { ApiError } from './types'
import {
  useAuthStore,
  getAuthCookie,
  getRefreshCookie,
  getOrgCookie,
  classifyMasterQuotechRefresh,
  type RefreshOutcome,
  redirectToMasterLogin as authRedirectToMasterLogin,
} from '@quotech/auth'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SetupHubApiOptions {
  /**
   * Human-readable hub name used in console warnings.
   * @example 'CRM Hub', 'CFO Hub'
   */
  hubName?: string
  /**
   * Test seam: overrides the silent master refresh attempted before the
   * session-expired redirect on a 401. Production never passes this —
   * it defaults to @quotech/auth's classifyMasterQuotechRefresh.
   */
  classifyRefresh?: () => Promise<RefreshOutcome>
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

let isRedirecting = false

function handleUnauthorised(_reason: string) {
  if (isRedirecting) return
  isRedirecting = true

  // Don't redirect if already on dashboard (master login page)
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'dashboard.quotech.ai'
  ) {
    isRedirecting = false
    return
  }

  try {
    authRedirectToMasterLogin('session_expired')
  } catch {
    // Fallback if @quotech/auth redirect fails
    useAuthStore.getState().clearAuth()
    if (typeof window !== 'undefined') {
      const returnTo = encodeURIComponent(window.location.href)
      window.location.replace(
        `https://dashboard.quotech.ai/signin?returnTo=${returnTo}&reason=session_expired`,
      )
    }
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Single in-flight silent-refresh promise (module scope, shared across the
 * hub's whole request fan-out). When N requests 401 together — the classic
 * post-idle burst — exactly ONE master refresh runs and all N retries wait
 * on its outcome. Mirrors the request-queue the CRM hub's private client
 * used to hand-roll (folded here in C4-MR4).
 */
let refreshInFlight: Promise<RefreshOutcome> | null = null

function runSharedRefresh(
  classify: () => Promise<RefreshOutcome>,
): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    refreshInFlight = classify()
      .catch((): RefreshOutcome => 'transient')
      .finally(() => {
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

type RetriableConfig = InternalAxiosRequestConfig & {
  _hubRetry?: boolean
  skipAuth?: boolean
}

/**
 * Initialise the global API client with cookie-first auth and a 401 interceptor.
 *
 * Call **once** in the hub's `main.tsx` before rendering the React tree.
 *
 * @example
 * ```ts
 * import { setupHubApi } from '@quotech/api'
 * setupHubApi({ hubName: 'CRM' })
 * ```
 */
export function setupHubApi(options: SetupHubApiOptions = {}): void {
  const { hubName = 'Hub', classifyRefresh = classifyMasterQuotechRefresh } = options

  const client = setupDefaultClient({
    getAccessToken: () => {
      // Cookie first (reliable during Zustand rehydration window)
      const cookieToken = getAuthCookie()
      if (cookieToken) return cookieToken

      // Zustand store fallback
      const storeToken = useAuthStore.getState().accessToken
      if (!storeToken) {
        console.warn(`[${hubName}] No access token available (cookie or store)`)
      }
      return storeToken
    },

    getRefreshToken: () => {
      const cookieRefresh = getRefreshCookie()
      if (cookieRefresh) return cookieRefresh
      return useAuthStore.getState().refreshToken
    },

    getOrgId: () => {
      const cookieOrg = getOrgCookie()
      if (cookieOrg?.id) return cookieOrg.id
      return useAuthStore.getState().organisationId
    },

    updateTokens: (access, refresh) =>
      useAuthStore.getState().updateTokens(access, refresh),

    clearAuth: () => useAuthStore.getState().clearAuth(),
  })

  // 401 interceptor — silent refresh → single retry → redirect only on a
  // DEFINITIVELY dead session (C4-MR4; union of the CRM hub's refresh queue
  // and the workforce djangoApi retry).
  //
  // NOTE: createApiClient's own error interceptor has already converted the
  // AxiosError into a plain ApiError by the time this runs — it now carries
  // `response`/`status` (before that bridge, `error.response?.status` was
  // always undefined here and this interceptor never fired at all).
  //
  // Outcome contract (BUG-2026-06-04 definitive-only sign-out):
  //   'refreshed' → retry the original request once with the fresh cookie;
  //                 if the retry 401s AGAIN the master session is alive but
  //                 this endpoint still refuses → endpoint-specific 401
  //                 (permissions / candidate-token probes like CRM's
  //                 validateToken) → reject to the caller, do NOT redirect
  //   'dead'      → the 30-day refresh credential is gone → master login
  //   'transient' → network/5xx blip: reject but PRESERVE the session
  client.interceptors.response.use(
    (response) => response,
    async (error: ApiError | { response?: { status?: number } }) => {
      const status = (error as ApiError).status || error.response?.status
      if (status !== 401) return Promise.reject(error)

      const original = (error as ApiError).originalError?.config as
        | RetriableConfig
        | undefined

      // No config to retry (non-request failure), unauthenticated-by-design
      // call, or the once-retried request → surface the 401 to the caller.
      if (!original || original.skipAuth || original._hubRetry) {
        return Promise.reject(error)
      }

      const outcome = await runSharedRefresh(classifyRefresh)
      if (outcome === 'refreshed') {
        // Fresh `quotech_auth` cookie is in the jar; the request
        // interceptor re-reads it (cookie-first) on the retry.
        original._hubRetry = true
        return client(original)
      }
      if (outcome === 'dead') {
        handleUnauthorised('session_expired')
      }
      return Promise.reject(error)
    },
  )
}

/**
 * Convenience re-export — identical to `getDefaultClient()` from `@quotech/api`.
 * Keeps backward-compatibility for hubs that import `getConfiguredClient`.
 */
export function getConfiguredClient() {
  return getDefaultClient()
}
